<script lang="ts">
  import * as Breadcrumb from '$lib/components/ui/breadcrumb/index.js'
  import { currentPath, servers as serversStore } from '$lib/store'
  import { describeSeries } from '$lib/apis/series/api'
  import { goto } from '@mateothegreat/svelte5-router'
  import House from '@lucide/svelte/icons/house'
  import { serverHash } from '$lib/utils'

  let { servers = [], libraries = {} }: {
    servers?: any[]
    libraries?: Record<string, any[]>
  } = $props()

  const UUID =
    '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}'

  type Crumb = {
    label: string
    href: string
    home?: boolean
    kind?: 'server' | 'library' | 'series' | 'settings'
    /** Dimmed `#NNNN` suffix (server uid tag). */
    tag?: string
  }

  let seriesTitleCache = $state<Record<string, string>>({})

  const path = $derived($currentPath)

  const crumbs = $derived.by(() => {
    const mediaMatch = path.match(new RegExp(`^/media/(${UUID})`))
    if (!mediaMatch) return [] as Crumb[]

    const mediaid = mediaMatch[1]
    const server =
      servers.find((s: any) => s.uid === mediaid) ??
      ($serversStore as Record<string, any>)[mediaid]
    const items: Crumb[] = [
      { label: 'Home', href: '/', home: true },
      {
        label: server?.name?.trim() || '…',
        tag: serverHash(mediaid),
        href: `/media/${mediaid}`,
        kind: 'server',
      },
    ]

    if (new RegExp(`/media/${UUID}/settings/?$`).test(path)) {
      items.push({
        label: 'Settings',
        href: `/media/${mediaid}/settings`,
        kind: 'settings',
      })
      return items
    }

    const libMatch = path.match(new RegExp(`/library/(${UUID})`))
    if (!libMatch) return items

    const libraryid = libMatch[1]
    const lib = (libraries[mediaid] ?? []).find((l: any) => l.id === libraryid)
    items.push({
      label: lib?.name ?? '…',
      href: `/media/${mediaid}/library/${libraryid}`,
      kind: 'library',
    })

    if (new RegExp(`/library/${UUID}/settings/?$`).test(path)) {
      items.push({
        label: 'Settings',
        href: `/media/${mediaid}/library/${libraryid}/settings`,
        kind: 'settings',
      })
      return items
    }

    const seriesMatch = path.match(new RegExp(`/series/(${UUID})`))
    if (!seriesMatch) return items

    const seriesid = seriesMatch[1]
    items.push({
      label: seriesTitleCache[seriesid] ?? '…',
      href: `/media/${mediaid}/library/${libraryid}/series/${seriesid}`,
      kind: 'series',
    })

    return items
  })

  $effect(() => {
    const mediaMatch = path.match(new RegExp(`^/media/(${UUID})`))
    const seriesMatch = path.match(new RegExp(`/series/(${UUID})`))
    if (!mediaMatch || !seriesMatch) return

    const mediaid = mediaMatch[1]
    const seriesid = seriesMatch[1]
    if (seriesTitleCache[seriesid]) return

    const server =
      servers.find((s: any) => s.uid === mediaid) ??
      ($serversStore as Record<string, any>)[mediaid]
    if (!server) return

    describeSeries(server, seriesid).then(r => {
      const title = r?.Series?.Infos?.title || r?.Series?.localTitle
      if (title) seriesTitleCache = { ...seriesTitleCache, [seriesid]: title }
    })
  })

  const kindLabel: Record<NonNullable<Crumb['kind']>, string> = {
    server: 'server',
    library: 'library',
    series: 'series',
    settings: 'settings',
  }

  const kindClass: Record<NonNullable<Crumb['kind']>, string> = {
    server: 'text-latte-blue dark:text-macchiato-blue',
    library: 'text-latte-peach dark:text-macchiato-peach',
    series: 'text-latte-mauve dark:text-macchiato-mauve',
    settings: 'text-latte-green dark:text-macchiato-green',
  }
</script>

{#snippet crumbLabel(crumb: Crumb)}
  <span class="truncate leading-tight">
    {crumb.label}{#if crumb.tag}<span class="font-normal opacity-55"> #{crumb.tag}</span>{/if}
  </span>
{/snippet}

{#if crumbs.length > 0}
  <div
    class="w-fit max-w-full rounded-md border border-latte-surface1/60 bg-latte-mantle/85 px-2.5 py-1 shadow-sm backdrop-blur-sm dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/85"
  >
    <Breadcrumb.Root class="min-w-0">
      <Breadcrumb.List class="flex-wrap items-end gap-y-1 pl-6 text-latte-text dark:text-macchiato-text sm:flex-nowrap sm:gap-y-0 sm:pl-0">
        {#each crumbs as crumb, i}
          {#if i > 0}
            <Breadcrumb.Separator class="self-end mb-0.5 text-latte-surface1 dark:text-macchiato-surface1" />
          {/if}
          <Breadcrumb.Item class="min-w-0 {i === 0 ? '-ml-6 sm:ml-0' : ''}">
            {#if i === crumbs.length - 1}
              <Breadcrumb.Page class="flex max-w-52 flex-col gap-x-0.5 text-latte-text dark:text-macchiato-text sm:max-w-72">
                {#if crumb.home}
                  <House class="size-4 shrink-0 -translate-y-0.5 text-latte-green dark:text-macchiato-green" aria-hidden="true" />
                  <span class="sr-only">Home</span>
                {:else}
                  {#if crumb.kind}
                    <span class="text-xs leading-none font-light {kindClass[crumb.kind]}">
                      {kindLabel[crumb.kind]}
                    </span>
                  {/if}
                  {@render crumbLabel(crumb)}
                {/if}
              </Breadcrumb.Page>
            {:else}
              <Breadcrumb.Link
                href={crumb.href}
                class="flex max-w-44 flex-col gap-x-0.5 text-latte-text/80 transition-colors hover:text-latte-mauve dark:text-macchiato-text/80 dark:hover:text-macchiato-mauve sm:max-w-56"
                onclick={(e) => {
                  e.preventDefault()
                  goto(crumb.href)
                }}
              >
                {#if crumb.home}
                  <House class="size-4 shrink-0 -translate-y-0.5 text-latte-green dark:text-macchiato-green" aria-hidden="true" />
                  <span class="sr-only">Home</span>
                {:else}
                  {#if crumb.kind}
                    <span class="text-xs leading-none font-light {kindClass[crumb.kind]}">
                      {kindLabel[crumb.kind]}
                    </span>
                  {/if}
                  {@render crumbLabel(crumb)}
                {/if}
              </Breadcrumb.Link>
            {/if}
          </Breadcrumb.Item>
        {/each}
      </Breadcrumb.List>
    </Breadcrumb.Root>
  </div>
{/if}
